import type {
  Note,
  TargetAppearance,
  TargetGesture,
  TargetResponse,
  TargetSize,
  TargetSpeed,
} from "./beatmap";
import {
  BLOCK_SIZE,
  HIT_RADIUS,
  SPAWN_Z,
  TARGET_STRIKE_Z,
  TRAVEL_TIME,
} from "./constants";

export interface TargetDefaults {
  size: TargetSize;
  speed: TargetSpeed;
  appearance: TargetAppearance;
}

export interface TargetSpec {
  archetype: TargetArchetype;
  gesture: TargetGesture;
  response: TargetResponse;
  direction: Note["dir"];
  size: TargetSize;
  speed: TargetSpeed;
  appearance: TargetAppearance;
  form: TargetForm;
  radius: number;
  bodySize: number;
  travelTime: number;
  travelSpeed: number;
  strikeZ: number;
  color: string;
  coreColor: string;
  accent: string;
  shadow: string;
  telegraphColor: string;
  bodyOpacity: number;
  auraOpacity: number;
  coreOpacity: number;
  highlightOpacity: number;
  glowScale: number;
  rimPower: number;
  lightIntensity: number;
  telegraphScalePulse: number;
  motionTrailOpacity: number;
}

export type TargetArchetype = TargetAppearance | "dodge" | "protected";
export type TargetForm = "orb" | "teardrop" | "hex" | "lotus";

export interface TargetVisualSpec {
  archetype: TargetArchetype;
  gesture: TargetGesture;
  response: TargetResponse;
  form: TargetForm;
  color: string;
  coreColor: string;
  accent: string;
  shadow: string;
  telegraphColor: string;
  bodyOpacity: number;
  auraOpacity: number;
  coreOpacity: number;
  highlightOpacity: number;
  glowScale: number;
  rimPower: number;
  lightIntensity: number;
  telegraphScalePulse: number;
  motionTrailOpacity: number;
}

const SIZE_SCALE: Record<TargetSize, number> = {
  large: 1.52,
  medium: 1.0,
  small: 0.66,
};

const SPEED_TRAVEL_TIME: Record<TargetSpeed, number> = {
  slow: 3.05,
  base: TRAVEL_TIME,
  fast: 2.15,
};

export const TARGET_ARCHETYPE_VISUALS: Record<
  TargetArchetype,
  TargetVisualSpec
> = {
  standard: {
    archetype: "standard",
    gesture: "cut",
    response: "strike",
    form: "orb",
    color: "#46A6C6",
    coreColor: "#B9F7FF",
    accent: "#EAFBFF",
    shadow: "#143A45",
    telegraphColor: "#F6B642",
    bodyOpacity: 0.72,
    auraOpacity: 0.2,
    coreOpacity: 0.28,
    highlightOpacity: 0.32,
    glowScale: 1.52,
    rimPower: 2.5,
    lightIntensity: 0.85,
    telegraphScalePulse: 0.075,
    motionTrailOpacity: 0,
  },
  fast: {
    archetype: "fast",
    gesture: "cut",
    response: "strike",
    // A spherical hit target keeps the visible body aligned with its generous
    // collision volume. Fast pressure is communicated by orange, motion and
    // pulse instead of a narrow silhouette that feels harder than it is.
    form: "orb",
    color: "#F26A2E",
    coreColor: "#FFD39A",
    accent: "#FFF0D4",
    shadow: "#5A190A",
    telegraphColor: "#F6B642",
    bodyOpacity: 0.74,
    auraOpacity: 0.24,
    coreOpacity: 0.34,
    highlightOpacity: 0.4,
    glowScale: 1.62,
    rimPower: 2.05,
    lightIntensity: 1.2,
    telegraphScalePulse: 0.12,
    motionTrailOpacity: 0.24,
  },
  dodge: {
    archetype: "dodge",
    gesture: "dodge",
    response: "evade",
    form: "hex",
    color: "#E0463C",
    coreColor: "#FFB3A5",
    accent: "#FFE8E4",
    shadow: "#4D0F0B",
    telegraphColor: "#F6B642",
    bodyOpacity: 0.7,
    auraOpacity: 0.18,
    coreOpacity: 0.22,
    highlightOpacity: 0.28,
    glowScale: 1.08,
    rimPower: 2.35,
    lightIntensity: 1.05,
    telegraphScalePulse: 0.08,
    motionTrailOpacity: 0,
  },
  protected: {
    archetype: "protected",
    gesture: "withhold",
    response: "stay-blade",
    form: "lotus",
    color: "#9D7CFF",
    coreColor: "#DCD0FF",
    accent: "#F6E7FF",
    shadow: "#30204F",
    telegraphColor: "#F6B642",
    bodyOpacity: 0.68,
    auraOpacity: 0.16,
    coreOpacity: 0.24,
    highlightOpacity: 0.3,
    glowScale: 1.38,
    rimPower: 2.8,
    lightIntensity: 0.75,
    telegraphScalePulse: 0.055,
    motionTrailOpacity: 0,
  },
};

export const DODGE_TARGET_SPEC = TARGET_ARCHETYPE_VISUALS.dodge;
export const PROTECTED_TARGET_SPEC = TARGET_ARCHETYPE_VISUALS.protected;

// One entry per campaign phase. Size and speed only rise after the previous
// motor skill has had time to consolidate; density is authored separately in
// the beatmaps and must not turn full katana cuts into controller flicks.
export const CAMPAIGN_TARGET_DEFAULTS: TargetDefaults[] = [
  { size: "large", speed: "slow", appearance: "standard" }, // I   Kamae
  { size: "large", speed: "base", appearance: "standard" }, // II  Kihon
  { size: "large", speed: "base", appearance: "standard" }, // III Suburi
  { size: "medium", speed: "base", appearance: "standard" }, // IV  Kesa
  { size: "medium", speed: "base", appearance: "standard" }, // V   Kiriage
  // VI/VII stay base speed — "fast" is reserved for phase IX (Zanshin,
  // "precision under pressure" in tutorials.ts), not built yet. Act II's
  // difficulty comes from chaining/kata-order complexity, not target speed.
  { size: "medium", speed: "base", appearance: "standard" }, // VI  Renzoku
  { size: "medium", speed: "base", appearance: "standard" }, // VII Kata
  // Act III opens with faster phrasing, but keeps medium targets so Happo can
  // assess eight-way flow without simultaneously shrinking the hit volume.
  // Small + fast first arrive together in IX's explicit precision-pressure
  // lesson, after vocabulary and chaining have both consolidated.
  { size: "medium", speed: "base", appearance: "standard" }, // VIII Happō
  { size: "small", speed: "fast", appearance: "fast" }, // IX  Zanshin
  { size: "small", speed: "fast", appearance: "fast" }, // X   Menkyo
];

export const SURVIVAL_TARGET_DEFAULTS: TargetDefaults = {
  size: "medium",
  speed: "base",
  appearance: "standard",
};

export function createTargetSpec(
  note: Note,
  defaults: TargetDefaults,
): TargetSpec {
  const size = note.size ?? defaults.size;
  const resolved = resolveCutTarget(note, defaults);
  const speed = resolved.speed;
  const appearance = resolved.appearance;
  const sizeScale = SIZE_SCALE[size];
  const travelTime = getTargetTravelTime(speed);
  const visual = getTargetVisualSpec(appearance);

  return {
    ...visual,
    direction: note.dir,
    size,
    speed,
    appearance,
    radius: HIT_RADIUS * sizeScale,
    bodySize: BLOCK_SIZE * sizeScale,
    travelTime,
    travelSpeed: Math.abs(TARGET_STRIKE_Z - SPAWN_Z) / travelTime,
    strikeZ: TARGET_STRIKE_Z,
  };
}

function resolveCutTarget(
  note: Note,
  defaults: TargetDefaults,
): { speed: TargetSpeed; appearance: TargetAppearance } {
  const rawSpeed = note.speed ?? defaults.speed;
  const rawAppearance = note.appearance ?? defaults.appearance;

  if (rawSpeed === "fast" || rawAppearance === "fast") {
    return { speed: "fast", appearance: "fast" };
  }

  return { speed: rawSpeed, appearance: "standard" };
}

export function getTargetVisualSpec(
  archetype: TargetArchetype,
): TargetVisualSpec {
  return TARGET_ARCHETYPE_VISUALS[archetype];
}

export function getTargetTravelTime(speed: TargetSpeed): number {
  return SPEED_TRAVEL_TIME[speed];
}
